import {testIdProps} from '@github-ui/test-id-props'
import {FilterIcon} from '@primer/octicons-react'
import {FormControl} from '@primer/react'
import {useId, useMemo, useRef} from 'react'

import {parseFullTextQuery} from '../../../components/filter-bar/helpers/search-filter'
import {isArchiveWorkflow} from '../../../helpers/workflow-utilities'
import {useWorkflowPermissions} from '../../../hooks/use-workflow-permissions'
import {useAutomationGraph} from '../../../state-providers/workflows/use-automation-graph'
import {WorkflowResources} from '../../../strings'
import {TokenizedFilterInput} from '../../filter-bar/tokenized-filter-input'
import {useGetProjectItemsAction} from '../hooks/use-get-project-items-action'
import {useSearchInputState} from '../hooks/use-search-input-state'
import {getSupportedProviderKeys, useWorkflowQueryValidation} from '../hooks/use-workflow-query-validation'
import {TimeBoundFilterWarning} from '../time-bound-filter-warning'
import {AutomationBlock} from './automation-block'
import {DisabledFilterInput} from './disabled-filter-input'
import styles from './get-project-items-block.module.css'

type TimeBoundFilter = {
  field: string
  unit: 'hours'
  value: number
}

const TIME_BOUND_FILTERS: {[field: string]: TimeBoundFilter} = {
  'last-updated': {
    field: 'last-updated',
    unit: 'hours',
    value: 12,
  },
  updated: {
    field: 'updated',
    unit: 'hours',
    value: 12,
  },
}

export const GetProjectItemsBlock = () => {
  const {workflow, initialQuery, localQuery, filterCount} = useAutomationGraph()
  const {hasWorkflowWritePermission} = useWorkflowPermissions()

  const {onSearchQueryChange} = useGetProjectItemsAction()

  const {isDirty, editingDisabled, resetChanges, onChangeQueryValue} = useSearchInputState({
    query: localQuery,
    initialQuery,
    workflow,
    onQueryChange: onSearchQueryChange,
  })
  const {onValidation, errorMessage} = useWorkflowQueryValidation(workflow, isDirty)

  const searchRef = useRef<HTMLInputElement>(null)

  const hasTimeBoundFilters = useMemo(() => isArchiveWorkflow(workflow), [workflow])

  const timedFilters = useMemo(() => {
    const filters = {} as {[field: string]: TimeBoundFilter}
    const parsedQuery = parseFullTextQuery(localQuery)

    for (const token of parsedQuery.orderedTokenizedFilters) {
      const timeBoundFilters = TIME_BOUND_FILTERS[token.type]
      if (timeBoundFilters && !filters[token.type]) {
        filters[token.type] = timeBoundFilters
      }
    }
    return Object.values(filters)
  }, [localQuery])

  const supportedProviderKeys = useMemo(() => getSupportedProviderKeys(workflow), [workflow])

  const filterValidationId = useId()
  const timeBoundFilterNotificationId = useId()
  const showTimeBoundNotification = hasTimeBoundFilters && timedFilters.length > 0

  return (
    <AutomationBlock
      icon={FilterIcon}
      iconBg="var(--bgColor-accent-muted)"
      iconColor="var(--fgColor-accent)"
      headerDescription={WorkflowResources.autoArchiveItemsLabel}
    >
      <FormControl className={styles.FormControl}>
        <FormControl.Label>Filters</FormControl.Label>
        {!editingDisabled ? (
          <TokenizedFilterInput
            value={localQuery}
            onChangeValue={onChangeQueryValue}
            inputRef={searchRef}
            filterCount={filterCount}
            onResetChanges={isDirty ? resetChanges : undefined}
            hideSaveButton
            hideDisplayButton
            aria-describedby={showTimeBoundNotification ? timeBoundFilterNotificationId : filterValidationId}
            limitedProviderKeys={supportedProviderKeys}
            onValidation={onValidation}
          />
        ) : (
          <DisabledFilterInput
            query={localQuery}
            filterCount={hasWorkflowWritePermission ? filterCount : undefined}
            icon={<FilterIcon />}
          />
        )}
        {errorMessage && (
          <FormControl.Validation variant="error" id="query-validation-message">
            {errorMessage}
          </FormControl.Validation>
        )}
      </FormControl>
      {showTimeBoundNotification && (
        <div id={timeBoundFilterNotificationId} className={styles.Box} {...testIdProps('time-bound-notification')}>
          {timedFilters.map(({field, value, unit}: TimeBoundFilter) => (
            <TimeBoundFilterWarning key={field} field={field} value={value} unit={unit} />
          ))}
        </div>
      )}
    </AutomationBlock>
  )
}
